import { Link } from "react-router-dom";
import { Clock, FileText, ShieldCheck, Wind, Check, MessageSquareQuote, MapPin, ExternalLink } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Reveal, SectionHead } from "./Motion";
import { ArrowLink, CtaPair, Frame, Stripes } from "./Bits";
import { BeforeAfter } from "./BeforeAfter";
import { BUSINESS, COTTAGE, FAQS, GTA, ILIAS_BIO, PROJECTS, PROMISES, STEPS, TIERS, WARRANTY, img } from "../data/site";

const PROMISE_ICONS = { dust: Wind, time: Clock, quote: FileText, warranty: ShieldCheck };

const PromiseCard = ({ p, className = "", big = false }) => {
  const Icon = PROMISE_ICONS[p.key];
  return (
    <Reveal className={`relative flex flex-col justify-between overflow-hidden rounded-md p-7 sm:p-9 ${className}`}>
      <Icon className={`h-8 w-8 ${big ? "text-flame" : "text-flame-600"}`} />
      <div className="mt-10">
        <h3 className={`font-extrabold tracking-tight ${big ? "text-3xl text-white sm:text-4xl" : "text-2xl text-navy"}`}>{p.title}</h3>
        <p className={`mt-3 max-w-md leading-relaxed ${big ? "text-slate-200" : "text-slate-600"}`}>{p.line}</p>
      </div>
    </Reveal>
  );
};

export const PromiseBento = () => (
  <section className="bg-paper py-24 sm:py-32" data-testid="amora-promise-section">
    <div className="container-x">
      <SectionHead eyebrow="The Amora Promise" title={<>Four promises. <span className="text-blue">In writing.</span></>} intro="Every Amora job is built on the same four commitments — the reason clients call us back for the next room, the next floor, the next building." />
      <div className="mt-14 grid gap-4 lg:grid-cols-12 lg:grid-rows-2">
        <Reveal className="relative min-h-[420px] overflow-hidden rounded-md bg-navy lg:col-span-7 lg:row-span-2">
          <img src={img.roller} alt="Paint roller applying a clean, even coat to a wall" className="absolute inset-0 h-full w-full object-cover opacity-45" loading="lazy" />
          <div className="absolute inset-0 bg-navy/50" />
          <div className="relative flex h-full flex-col justify-between p-7 sm:p-10">
            <div className="flex items-center justify-between">
              <Wind className="h-9 w-9 text-flame" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-200">The Amora Dust-Free Promise™</span>
            </div>
            <div>
              <h3 className="text-4xl font-extrabold leading-[0.95] tracking-tight text-white sm:text-6xl">Dust-Free<br />Process</h3>
              <p className="mt-4 max-w-md text-base leading-relaxed text-slate-100">{PROMISES[0].line}</p>
            </div>
          </div>
        </Reveal>
        <PromiseCard p={PROMISES[1]} className="border border-slate-200 bg-white lg:col-span-5" />
        <div className="grid gap-4 sm:grid-cols-2 lg:col-span-5">
          <PromiseCard p={PROMISES[2]} className="bg-blue [&_h3]:!text-white [&_p]:!text-slate-100 [&_svg]:!text-white" />
          <PromiseCard p={PROMISES[3]} className="border border-slate-200 bg-white" />
        </div>
      </div>
    </div>
  </section>
);

const TrackCard = ({ eyebrow, title, proof, to, image, alt, testId }) => (
  <Reveal className="group relative">
    <Link to={to} data-testid={testId} className="block">
      <Frame src={image} alt={alt} className="aspect-[4/5] rounded-md sm:aspect-[5/4]" imgClass="opacity-90" />
      <div className="absolute inset-0 rounded-md bg-gradient-to-t from-navy via-navy/60 to-navy/5" />
      <div className="absolute inset-x-0 bottom-0 p-6 sm:p-9">
        <p className="eyebrow text-flame">{eyebrow}</p>
        <h3 className="mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-5xl">{title}</h3>
        <p className="mt-3 max-w-md text-sm text-slate-200 sm:text-base">{proof}</p>
        <span className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-bold text-navy transition-colors group-hover:bg-flame">Explore {eyebrow.toLowerCase()} →</span>
      </div>
    </Link>
  </Reveal>
);

export const TwoTrack = () => (
  <section className="bg-paper pb-24 sm:pb-32" data-testid="two-track-section">
    <div className="container-x grid gap-5 md:grid-cols-2">
      <TrackCard testId="track-residential-link" eyebrow="Residential" title="Homes & condos" to="/residential" image={img.livingTall} alt="Bright living room with freshly painted white walls"
        proof="Family homes, condos and cottages — painted while you live in them, clean at the end of every day." />
      <TrackCard testId="track-commercial-link" eyebrow="Commercial" title="Offices & buildings" to="/commercial" image={img.office} alt="Modern office interior with clean painted walls"
        proof="Trusted by teams at RBC, TD Bank, Tim Hortons and SickKids Hospital. After-hours scheduling across Ontario." />
    </div>
  </section>
);

export const HowItWorks = ({ dark = true }) => (
  <section className={`relative overflow-hidden py-24 sm:py-32 ${dark ? "bg-navy grain" : "bg-white"}`} data-testid="how-it-works-section">
    <div className="container-x relative">
      <SectionHead dark={dark} eyebrow="How it works" title="One call. One visit. One written quote in 24 hours." />
      <ol className={`mt-16 grid gap-px overflow-hidden rounded-md md:grid-cols-2 lg:grid-cols-4 ${dark ? "bg-white/10" : "bg-slate-200"}`}>
        {STEPS.map((s, i) => (
          <Reveal as="li" key={s.n} delay={i * 0.08} className={`p-7 sm:p-8 ${dark ? "bg-navy" : "bg-white"}`}>
            <span className="font-display text-6xl font-extrabold text-flame">{s.n}</span>
            <h3 className={`mt-8 text-xl font-bold ${dark ? "text-white" : "text-navy"}`}>{s.title}</h3>
            <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>{s.line}</p>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

export const SliderShowcase = ({ items = PROJECTS.slice(0, 2), title = "Drag to see the difference.", eyebrow = "Before / After" }) => (
  <section className="bg-white py-24 sm:py-32" data-testid="before-after-section">
    <div className="container-x">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHead eyebrow={eyebrow} title={title} />
        <ArrowLink to="/projects" testId="sliders-all-projects-link">See all projects</ArrowLink>
      </div>
      <div className="mt-14 grid gap-10 md:grid-cols-2">
        {items.map((p, i) => <Reveal key={p.id} delay={i * 0.1}><BeforeAfter {...p} testId={`before-after-slider-${p.id}`} /></Reveal>)}
      </div>
    </div>
  </section>
);

export const FinishTiers = () => (
  <section className="bg-paper py-24 sm:py-32" data-testid="finish-tiers-section">
    <div className="container-x">
      <SectionHead eyebrow="Finish tiers" title="Pick your level. We'll confirm it on site." intro="Three standards of work so you can self-select your scope before the visit. Ilias confirms the right tier for each surface — your written quote spells out exactly what's included." />
      <div className="mt-14 grid gap-4 lg:grid-cols-3">
        {TIERS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.08} className={`relative flex flex-col rounded-md p-8 ${t.featured ? "bg-navy text-white" : "border border-slate-200 bg-white"}`}>
            <div data-testid={`tier-${t.name.toLowerCase()}`}>
              {t.featured && <span className="absolute right-6 top-6 rounded-full bg-flame px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-navy">Our standard</span>}
              <p className={`font-mono text-xs uppercase tracking-widest ${t.featured ? "text-flame" : "text-flame-600"}`}>{t.tag}</p>
              <h3 className={`mt-3 text-4xl font-extrabold tracking-tight ${t.featured ? "text-white" : "text-navy"}`}>{t.name}</h3>
              <p className={`mt-3 text-sm leading-relaxed ${t.featured ? "text-slate-300" : "text-slate-600"}`}>{t.line}</p>
              <ul className="mt-8 space-y-3">
                {t.items.map((x) => (
                  <li key={x} className={`flex gap-3 text-sm ${t.featured ? "text-slate-100" : "text-navy"}`}><Check className="mt-0.5 h-4 w-4 shrink-0 text-flame" />{x}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="mt-10"><CtaPair dark={false} testPrefix="tiers" /></div>
    </div>
  </section>
);

export const FounderPortrait = ({ className = "" }) => (
  <div className={`group relative overflow-hidden rounded-md bg-blue ${className}`} data-testid="ilias-portrait-card">
    <img src="/images/ilias.jpg" alt="Ilias Lagogiannis, founder and lead estimator of Amora Painting" className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-[1.4s] ease-out group-hover:scale-[1.03]" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent" />
    <div className="relative flex h-full flex-col justify-end p-7 sm:p-9">
      <p className="font-mono text-xs uppercase tracking-[0.22em] text-white/85">Founder · Lead Estimator</p>
      <p className="mt-2 font-display text-3xl font-extrabold leading-[0.95] tracking-tight text-white sm:text-4xl">Ilias Lagogiannis</p>
      <div className="mt-5 inline-flex items-center gap-3 self-start rounded-full bg-flame px-4 py-2 text-sm font-bold text-navy">
        <span className="font-display text-lg font-extrabold">30</span> years on the tools
      </div>
    </div>
    <Stripes className="absolute bottom-0 left-0 h-2 w-full" />
  </div>
);

export const Estimator = () => (
  <section className="bg-navy py-24 sm:py-32" data-testid="estimator-section">
    <div className="container-x grid items-center gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-5"><FounderPortrait className="aspect-[4/5]" /></Reveal>
      <div className="lg:col-span-7">
        <Reveal><p className="eyebrow text-flame">Meet your estimator</p></Reveal>
        <Reveal delay={0.05}><h2 className="mt-5 text-4xl font-extrabold tracking-tight text-white sm:text-6xl">{BUSINESS.founder}</h2></Reveal>
        <Reveal delay={0.1}><p className="mt-3 font-mono text-sm uppercase tracking-widest text-slate-300">{BUSINESS.founderTitle}</p></Reveal>
        <Reveal delay={0.15}><p className="mt-8 text-lg leading-relaxed text-slate-200">{ILIAS_BIO}</p></Reveal>
        <Reveal delay={0.2}><p className="mt-8 border-l-4 border-flame pl-5 font-display text-2xl font-bold text-white">Ilias personally scopes every project.</p></Reveal>
        <Reveal delay={0.25} className="mt-10"><CtaPair testPrefix="estimator" bookLabel="Book a visit with Ilias" /></Reveal>
      </div>
    </div>
  </section>
);

const STANDARDS = [
  { k: "On time", h: "They finished on the day they said they would.", d: "The finish date in your written quote is the one we build the job around." },
  { k: "Dust-free", h: "You'd never know a crew was here.", d: "Sealed zones and a daily clean-down, so your space is usable every evening." },
  { k: "Detail", h: "Every line is perfect.", d: "Hand-cut edges, proper prep and a final walkthrough with touch-ups before we leave." },
];

export const ReviewsWall = ({ compact = false }) => (
  <section className="bg-white py-24 sm:py-32" data-testid="reviews-wall-section">
    <div className="container-x">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <SectionHead eyebrow="Reviews" title="The review we work to earn." intro="Every Amora job ends with a walkthrough — and an invitation to tell others how it went. These are the three things we want every client to be able to say." />
        <Reveal className="rounded-md bg-navy p-6 text-white">
          <div data-testid="reviews-aggregate">
            <MessageSquareQuote className="h-7 w-7 text-flame" />
            <p className="mt-3 font-display text-lg font-bold">Read what clients say on Google</p>
            <a href={BUSINESS.googleReviewUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-flame hover:underline" data-testid="reviews-google-read-link">See Google reviews <ExternalLink className="h-3.5 w-3.5" /></a>
          </div>
        </Reveal>
      </div>
      <div className="mt-12 grid gap-4 md:grid-cols-3">
        {STANDARDS.map((s, n) => (
          <Reveal key={s.k} delay={n * 0.06} className="rounded-md border border-slate-200 bg-paper p-8">
            <div data-testid={`review-standard-${n + 1}`}>
              <p className="font-mono text-[11px] uppercase tracking-widest text-flame-600">The standard · {s.k}</p>
              <p className="mt-5 font-display text-2xl font-extrabold leading-tight text-navy">{s.h}</p>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="mt-10 flex flex-wrap items-center gap-6">
        <a href={BUSINESS.googleReviewUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost-light" data-testid="review-us-google-btn">Worked with us? Review us on Google <ExternalLink className="h-4 w-4" /></a>
        {compact && <ArrowLink to="/reviews" testId="reviews-page-link">Reviews page</ArrowLink>}
      </div>
    </div>
  </section>
);

export const FaqBlock = ({ faqs = FAQS, title = "Straight answers.", eyebrow = "FAQ" }) => (
  <section className="bg-paper py-24 sm:py-32" data-testid="faq-section">
    <div className="container-x grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-4"><SectionHead eyebrow={eyebrow} title={title} intro="Still unsure? Ask Ilias at your free visit — or call." /></div>
      <Reveal className="lg:col-span-8">
        <Accordion type="single" collapsible className="border-t border-slate-300">
          {faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`f${i}`} className="border-slate-300">
              <AccordionTrigger className="py-6 text-left font-display text-lg font-bold text-navy hover:no-underline sm:text-xl" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="pb-6 text-base leading-relaxed text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

const citySlug = (n) => n.toLowerCase().replace(/\s+/g, "-");
const CityLink = ({ name }) => (
  <Link to={`/painters/${citySlug(name)}`} className="underline decoration-slate-300 decoration-1 underline-offset-4 transition-colors hover:text-flame-600 hover:decoration-flame" data-testid={`service-area-link-${citySlug(name)}`}>{name}</Link>
);

export const ServiceArea = () => {
  const row = [...GTA, ...COTTAGE];
  return (
    <section className="overflow-hidden bg-white py-24 sm:py-32" data-testid="service-area-section">
      <div className="container-x">
        <SectionHead eyebrow="Service area" title="All of the GTA. Cottage country. Commercial across Ontario." />
        <div className="mt-6"><ArrowLink to="/service-areas" testId="service-areas-index-link">Find your city</ArrowLink></div>
      </div>
      <div className="relative mt-14 border-y border-slate-200 py-8" aria-hidden="true">
        <div className="flex w-max animate-marquee gap-10 whitespace-nowrap">
          {[...row, ...row].map((c, i) => (
            <span key={`${c}-${i}`} className={`font-display text-5xl font-extrabold tracking-tight sm:text-7xl ${i % 2 ? "text-outline" : "text-navy"}`}>{c} <span className="text-flame">/</span></span>
          ))}
        </div>
      </div>
      <div className="container-x mt-14 grid gap-10 md:grid-cols-3">
        <Reveal>
          <p className="eyebrow flex items-center gap-2 text-flame-600"><MapPin className="h-4 w-4" /> Greater Toronto Area</p>
          <p className="mt-4 flex flex-wrap gap-x-3 gap-y-1.5 text-sm leading-relaxed text-slate-700">{GTA.map((c) => <CityLink key={c} name={c} />)}</p>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="eyebrow flex items-center gap-2 text-flame-600"><MapPin className="h-4 w-4" /> Ontario cottage country</p>
          <p className="mt-4 flex flex-wrap gap-x-3 gap-y-1.5 text-sm leading-relaxed text-slate-700">{COTTAGE.map((c) => <CityLink key={c} name={c} />)}</p>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="eyebrow flex items-center gap-2 text-flame-600"><MapPin className="h-4 w-4" /> Commercial</p>
          <p className="mt-4 text-sm leading-relaxed text-slate-700">Commercial projects across Ontario — offices, hospitality, retail, high-rise and multi-unit buildings.</p>
        </Reveal>
      </div>
    </section>
  );
};

export const FinalCta = () => (
  <section className="relative overflow-hidden bg-blue py-24 sm:py-32" data-testid="final-cta-section">
    <div className="container-x relative">
      <span className="inline-flex rounded bg-navy p-1.5"><Stripes className="h-4 w-16" /></span>
      <Reveal><h2 className="mt-8 max-w-4xl text-4xl font-extrabold leading-[0.98] tracking-tight text-white sm:text-6xl">Book your free Colour & Scope Visit™.</h2></Reveal>
      <Reveal delay={0.08}><p className="mt-6 max-w-2xl text-lg text-white/90">30 minutes, in person, with Ilias. A written quote in your inbox within 24 hours. No pressure, no obligation — free to book.</p></Reveal>
      <Reveal delay={0.16} className="mt-10"><CtaPair testPrefix="final-cta" /></Reveal>
    </div>
  </section>
);

export const ClientStrip = () => (
  <section className="bg-navy py-16" data-testid="client-strip">
    <div className="container-x flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
      <p className="max-w-xs text-sm font-semibold text-slate-300">Trusted by teams at</p>
      <ul className="flex flex-wrap gap-x-10 gap-y-4">
        {BUSINESS.clients.map((c) => <li key={c} className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">{c}</li>)}
      </ul>
    </div>
  </section>
);

export const WarrantyModule = () => (
  <section className="bg-white py-20" data-testid="warranty-module">
    <div className="container-x grid gap-8 rounded-md border border-slate-200 p-8 sm:p-12 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <ShieldCheck className="h-10 w-10 text-brick" />
        <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-navy">{WARRANTY.title}</h2>
      </div>
      <div className="space-y-4 text-slate-700 lg:col-span-8">
        <p data-testid="warranty-commercial-text"><span className="font-bold text-navy">Commercial — </span>{WARRANTY.commercial}</p>
        <p data-testid="warranty-residential-text"><span className="font-bold text-navy">Residential — </span>{WARRANTY.residential}</p>
        <p className="text-sm text-slate-600">{WARRANTY.note}</p>
      </div>
    </div>
  </section>
);
